import { useApi } from '@/src/store/api.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChat } from '@/src/store/jam-chat.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useUser } from '@/src/store/user.context';
import { JamSupportChat } from '@/src/types/JamSupportChat';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { sanitizeSupportChats } from '@/src/utils/support-chat-queue.utils';
import { Box, Button, ButtonDropdown, ContentLayout, Header, Table } from '@amzn/awsui-components-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FacilitatorSupportChats = () => {
  const { t } = useTranslation();
  const { eventName } = useJamEventDetails();
  const { clearFlashbars } = useFlashbars();
  const {
    loadAllSupportChats,
    allSupportChats,
    supportChats: activeSupportChats,
    setSelectedSupportChatId,
    setActiveChatType,
    setViewAllSupportChats,
    updateSupportChatReadCounter,
  } = useJamChat();
  const { user } = useUser();
  const { jamSupportChatApi } = useApi();

  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const supportChats = useMemo((): JamSupportChat[] => {
    if (!allSupportChats.length || !user) {
      return [];
    }
    return sanitizeSupportChats(allSupportChats);
  }, [allSupportChats]);

  const beginChat = async (id: string) => {
    try {
      clearFlashbars();
      setIsLoading(true);
      await jamSupportChatApi.selfAssignSupportChat(id, true);
      setTimeout(() => {
        setSelectedSupportChatId(id);
        setActiveChatType('support');
      }, 0);

      // Set a small delay after accepting the chat to refresh the chat list
      setTimeout(() => {
        void loadAllSupportChats(eventName);
      }, 1000);
    } catch (e) {
      // Error handled by API.
    }
    setIsLoading(false);
  };

  const openChat = (id: string) => {
    setSelectedSupportChatId(id);
    setActiveChatType('support');
    setViewAllSupportChats(true);
    updateSupportChatReadCounter(id, 0);
  };

  const initialize = async () => {
    try {
      clearFlashbars();
      setIsFetching(true);
      await loadAllSupportChats(eventName);
    } catch (e) {
      // error handled
    }
    setIsFetching(false);
  };

  useEffect(() => {
    void initialize();
  }, []);

  // Refresh the chat list every time one of the chats ends
  // The chat list will also be updated every time a websocket message is received
  useEffect(() => {
    void loadAllSupportChats(eventName);
  }, [activeSupportChats?.length]);

  return (
    <div>
      <ContentLayout header={<Header variant="h1">{t(i18nKeys.facilitator.supportChats.title)}</Header>}>
        <Table
          header={
            <Header description={t(i18nKeys.facilitator.supportChats.caption)}>
              {t(i18nKeys.facilitator.supportChats.subTitle)}
            </Header>
          }
          items={supportChats}
          columnDefinitions={[
            {
              id: 'time',
              header: t(i18nKeys.facilitator.supportChats.list.titles.time),
              cell: (item) => (item.timestamp ? moment(item.timestamp).format('MM/DD/YYYY hh:mm A') : '--'),
            },
            {
              id: 'user',
              header: t(i18nKeys.facilitator.supportChats.list.titles.user),
              cell: (item) => item.creatorDisplayName,
            },
            {
              id: 'team',
              header: t(i18nKeys.facilitator.supportChats.list.titles.team),
              cell: (item) => item.creatorTeamName || '--',
            },
            {
              id: "challengeTitle",
              header: t(i18nKeys.challenges.challengeDetails.titles.challengeTitle),
              cell: (item) => item?.challengeTitle || '--',
            },
            {
              id: 'chat-status',
              header: t(i18nKeys.facilitator.supportChats.list.titles.chatStatus),
              cell: (item) =>
                item.assignedToDisplayName ? (
                  <ButtonDropdown items={[
                      {
                        text: t(i18nKeys.facilitator.supportChats.actions.unassignChat) || "",
                        id: "unassign-chat"
                      }
                    ]}
                    mainAction={{
                      text: t(i18nKeys.facilitator.supportChats.actions.viewChat) || "",
                      onClick: () => openChat(item.id)
                    }}
                    data-testid="support-chat-view-chat"
                    loading={isLoading}
                    expandToViewport
                    onItemClick={async (event) => {
                      if (event.detail.id === "unassign-chat") {
                        await jamSupportChatApi.unassignSupportChat(item, false);
                        setTimeout(() => {
                          void loadAllSupportChats(eventName);
                        }, 1000);
                      }
                    }}
                  />
                ) : (
                  <Button
                    data-testid="support-chat-begin-chat"
                    loading={isLoading}
                    variant="inline-link"
                    onClick={() => void beginChat(item.id)}
                  >
                    {t(i18nKeys.facilitator.supportChats.actions.beginChat)}
                  </Button>
                ),
            },
          ]}
          empty={
            <Box textAlign="center">
              <b> {t(i18nKeys.facilitator.supportChats.list.empty)}</b>
            </Box>
          }
          loading={isFetching}
        />
      </ContentLayout>
    </div>
  );
};

export default FacilitatorSupportChats;
